import { html, render } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { Dialog } from '@vaadin/vaadin-core'
import { View } from '../view'
import { BookStore } from '../../stores'
import { validateAll } from '../../vaadin'
import { inject, Symbols } from '../../di'
import { OpenLibrary, OpenLibraryList } from '../../stores/openLibrary'
import { blankCover } from './blank'

@customElement('kob-book-match')
export class BookMatch extends View {

    @property({ type: Object })
    bookStore: BookStore

    @inject(Symbols.stores.openLibraryList)
    googleBooks: OpenLibraryList

    @property()
    dialogOpened

    formRoot

    render() {
        return html`
            <vaadin-dialog theme="kob-modal" aria-label="Match Book" .opened="${this.dialogOpened}"
                @opened-changed="${this.handleOpenChanged}" .renderer="${this.renderContent}">
            </vaadin-dialog>
        `
    }

    handleDone = () => this.dialogOpened = false

    handleOpenChanged = async (e: CustomEvent) => {
        this.dialogOpened = e.detail.value
        if (this.dialogOpened) {
            const initial = this.bookStore.ISBN
                || [this.bookStore.title, this.bookStore.author].filter(Boolean).join(' ')
            this.googleBooks.setSearch(initial || '')
        }
        else
            this.googleBooks.reset()
    }

    handleSearchChanged = (e) => this.googleBooks.setSearch(e.target.value)

    renderContent = (root: HTMLElement, dialog?: Dialog) => {
        this.formRoot = root
        render(
            html`
                <kob-busy .stores=${[this.googleBooks]}></kob-busy>
                <div class="modal wide">
                    <div class="modal-head">
                        <div>
                            <h3 class="modal-title">Match book</h3>
                            <p class="modal-sub">
                                Results from Open Library — pick the right edition to fix the cover and details.
                            </p>
                        </div>
                        <vaadin-button class="modal-close" aria-label="Close" @click="${this.handleDone}">
                            <vaadin-icon icon="lumo:cross"></vaadin-icon>
                        </vaadin-button>
                    </div>

                    <div class="modal-body">
                        <div class="modal-banner">
                            <vaadin-icon icon="vaadin:warning"></vaadin-icon>
                            <div>
                                Changing the ISBN or title while the book is on your Kobo can create
                                duplicates the next time it syncs.
                            </div>
                        </div>
                    </div>

                    <div class="match-search-row">
                        <vaadin-text-field
                            class="search"
                            placeholder="Title, author, or ISBN"
                            clear-button-visible
                            value=${this.googleBooks.filter?.query}
                            @change=${this.handleSearchChanged}
                        ></vaadin-text-field>
                        <span class="count">
                            Showing <strong>${this.googleBooks.loaded}</strong> of <strong>${this.googleBooks.total || 0}</strong>
                        </span>
                    </div>

                    <div class="match-results">
                        ${this.googleBooks.stores.map(store => this.renderResult(store as OpenLibrary))}
                    </div>

                    <div class="modal-foot right">
                        <vaadin-button theme="primary" @click="${this.handleDone}">Done</vaadin-button>
                    </div>
                </div>
            `,
            root
        )
    }

    renderResult = (store: OpenLibrary) => {
        const isCurrent = !!store.ISBN && store.ISBN === this.bookStore?.ISBN

        const selectMatch = async () => {
            await this.bookStore.matchBook(store)
            this.dialogOpened = false
        }

        return html`
            <article class="match-result ${isCurrent ? 'is-current' : ''}">
                <div class="cover">
                    ${blankCover}
                    ${store.coverURL
                        ? html`<img src=${store.coverURL} alt="" loading="lazy" @error=${this.hideCover} />`
                        : ''}
                </div>
                <div>
                    <div class="m-title">
                        ${store.title}
                        ${isCurrent ? html`<span class="m-current-flag">Current match</span>` : ''}
                    </div>
                    <div class="m-author">${store.author}</div>
                    ${store.ISBN ? html`<span class="m-isbn">ISBN ${store.ISBN}</span>` : ''}
                </div>
                <vaadin-button theme="small" @click=${selectMatch}>Select</vaadin-button>
            </article>
        `
    }

    /** Open Library has no cover for every edition — uncover the blank jacket behind it. */
    private hideCover = (e: Event) => {
        (e.target as HTMLElement).style.display = 'none'
    }

    async connectedCallback() {
        await super.connectedCallback()
        this.reaction(
            () => [this.googleBooks.filter, this.googleBooks.stores],
            () => this.formRoot ? this.renderContent(this.formRoot) : undefined
        )

    }
}